import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Spin } from 'antd';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import StatusHistory from './StatusHistory';
import { fetchStatusHistory, fetchStatusHistoryV2 } from 'store/advancePaymentSlice';
import { fetchFinanceRequestStatusHistory } from 'store/financeSlice';
import { fetchFinanceRequestStatusHistoryV2 } from 'store/financeSliceV2';
import { getUIVersion } from 'utils/router-static-data';

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
		padding: 0;
	}
	.ant-modal-body {
		padding: 24px;
		max-height: 70vh;
		overflow-y: auto;
	}
	.ant-modal-header {
		border-bottom: 1px solid #f0f0f0;
		padding: 20px 24px;
		position: relative;
	}
	.ant-modal-title {
		font-family: 'Inter', sans-serif;
		font-weight: 600;
		font-size: 18px;
		color: #000;
		margin: 0;
	}
`;

const LoadingContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 200px;
`;

function StatusHistoryModal({
	visible,
	onClose,
	adhocRequestId,
	fetchStatusHistoryAction = 'adhoc',
}) {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const [historyData, setHistoryData] = useState([]);

	useEffect(() => {
		if (visible && adhocRequestId) {
			fetchHistory();
		} else {
			setHistoryData([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, adhocRequestId, fetchStatusHistoryAction]);

	const fetchHistory = async () => {
		setLoading(true);
		try {
			let response;
			if (fetchStatusHistoryAction === 'finance_v2') {
				const result = await dispatch(
					fetchFinanceRequestStatusHistoryV2({ finance_request_id: adhocRequestId })
				);
				setHistoryData(result?.status_history || []);
				setLoading(false);
				return;
			}
			if (fetchStatusHistoryAction === 'finance') {
				response = await dispatch(
					fetchFinanceRequestStatusHistory({ finance_request_id: adhocRequestId })
				);
			} else {
				// Use v2 API for adhoc requests when in new UI, otherwise use v1
				const isV2UI = getUIVersion() === 'new';
				if (isV2UI) {
					response = await dispatch(fetchStatusHistoryV2({ id: adhocRequestId }));
				} else {
					response = await dispatch(fetchStatusHistory({ id: adhocRequestId }));
				}
			}

			if (response?.data) {
				setHistoryData(response.data);
			} else {
				setHistoryData([]);
			}
		} catch (error) {
			console.error('Error fetching status history:', error);
			setHistoryData([]);
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setHistoryData([]);
		onClose();
	};

	return (
		<StyledModal
			open={visible}
			onCancel={handleClose}
			footer={null}
			centered
			width={600}
			closable
			title="Status History"
		>
			{loading ? (
				<LoadingContainer>
					<Spin size="large" tip="Loading status history..." />
				</LoadingContainer>
			) : (
				<StatusHistory historyData={historyData} />
			)}
		</StyledModal>
	);
}

StatusHistoryModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	adhocRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	fetchStatusHistoryAction: PropTypes.oneOf(['adhoc', 'finance', 'finance_v2']),
};

export default StatusHistoryModal;
